import { useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import type { LogtoDataProvider } from '../../dataProvider';
import type { SettingsField, TemplateSecretStatus } from '../../types';
import { apiErrorMessage } from './apiError';

interface Props {
  clientId: string;
  templateKey: string;
  field: SettingsField;
  status?: TemplateSecretStatus;
  required: boolean;
  disabled?: boolean;
  onChanged: (status: TemplateSecretStatus | null) => void;
}


// Secret text and file settings are saved immediately through their own
// endpoint (never as part of the configuration) and are never shown back.
export const TemplateSecretInput = ({ clientId, templateKey, field, status, required, disabled, onChanged }: Props) => {
  const dataProvider = useDataProvider<LogtoDataProvider>();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const hasValue = !!status?.hasValue;
  const meta = (status?.metadata || {}) as Record<string, unknown>;
  const inputId = `template-file-${field.key}`;

  const run = async (action: () => Promise<TemplateSecretStatus | null>, success: string) => {
    setBusy(true);
    setError(null);
    try {
      onChanged(await action());
      notify(success, { type: 'success' });
      return true;
    } catch (e) {
      setError(apiErrorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveText = async () => {
    const ok = await run(() => dataProvider.setTemplateSecret(clientId, field.key, value, templateKey), `${field.label || field.key}: сохранено`);
    if (ok) {
      setValue('');
      setDialogOpen(false);
    }
  };

  const upload = async (file: File | undefined) => {
    if (!file) return;
    await run(() => dataProvider.uploadTemplateFile(clientId, field.key, file, templateKey), `${file.name}: загружен`);
  };

  const remove = () =>
    run(async () => {
      await dataProvider.deleteTemplateSecret(clientId, field.key, templateKey);
      return null;
    }, `${field.label || field.key}: удалено`);

  return (
    <Box sx={{ mb: 2 }}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="body1">
          {field.label || field.key}
          {required && ' *'}
        </Typography>
        {field.platform && <Chip label={field.platform} size="small" variant="outlined" />}
        <Chip
          size="small"
          color={hasValue ? 'success' : required ? 'warning' : 'default'}
          label={hasValue ? (field.type === 'file' ? 'загружен' : 'задан') : 'не задан'}
        />
      </Box>

      {hasValue && field.type === 'file' && (
        <Typography variant="caption" color="text.secondary" display="block">
          {[meta.file_name, meta.project_id && `проект ${meta.project_id}`, meta.bundle_id && `bundle ${meta.bundle_id}`]
            .filter(Boolean)
            .join(' · ')}
          {status?.updatedAt && ` · ${new Date(status.updatedAt).toLocaleString()}`}
        </Typography>
      )}
      {hasValue && field.type === 'secret' && status?.updatedAt && (
        <Typography variant="caption" color="text.secondary" display="block">
          обновлён {new Date(status.updatedAt).toLocaleString()}
        </Typography>
      )}
      {field.help && (
        <Typography variant="caption" color="text.secondary" display="block">
          {field.help}
        </Typography>
      )}

      <Box display="flex" gap={1} sx={{ mt: 1 }}>
        {field.type === 'file' ? (
          <>
            <input
              id={inputId}
              type="file"
              accept={field.accept}
              style={{ display: 'none' }}
              disabled={disabled || busy}
              onChange={(e) => {
                void upload(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <label htmlFor={inputId}>
              <Button
                component="span"
                variant="outlined"
                size="small"
                disabled={disabled || busy}
                startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              >
                {hasValue ? 'Заменить файл' : 'Загрузить файл'}
              </Button>
            </label>
          </>
        ) : (
          <Button variant="outlined" size="small" disabled={disabled || busy} onClick={() => setDialogOpen(true)}>
            {hasValue ? 'Изменить' : 'Задать'}
          </Button>
        )}
        {hasValue && (
          <Button variant="text" size="small" color="error" disabled={disabled || busy} onClick={() => void remove()}>
            Удалить
          </Button>
        )}
      </Box>
      {error && !dialogOpen && (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{field.label || field.key}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            type="password"
            autoComplete="off"
            label="Новое значение"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={!!error}
            helperText={error || 'Значение не отображается после сохранения'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={() => void saveText()} disabled={busy || value.trim() === ''}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
